// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import LoadingOverlay, { LoadingOverlayProps } from "@insite/mobius/LoadingOverlay";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@rc/App/Components/Notify";
import checkSubscription from "@rc/App/Handlers/JoinToday/checkSubscription";
import subscribeUser from "@rc/App/Handlers/JoinToday/subscribeUser";
import { useContext } from "react";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapDispatchToProps = {
    checkSubscription,
    subscribeUser,
};

type Props = ResolveThunks<typeof mapDispatchToProps>;

export interface JoinTodayStyles {
    container?: InjectableCss;
    loadingOverlay?: LoadingOverlayProps;
    joinWrapper?: InjectableCss;
    heading?: TypographyProps;
    joinInfo?: TypographyProps;
    joinErrorMessage?: TypographyProps;
    agreeToJoinMessage?: TypographyProps;
    UpdatePrefrencesMessage?: TypographyProps;
}

export const joinTodayStyles: JoinTodayStyles = {};

const styles = joinTodayStyles;

const JoinToday = (props: Props) => {
    const { checkSubscription, subscribeUser } = props;
    const [isSubscribed, setIsSubscribed] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isSuccessfullySubscribed, setIsSuccessfullySubscribed] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState("");

    const toasterContext = useContext(ToasterContext);

    useEffect(() => {
        setIsLoading(true);
        checkSubscription({
            onSuccess(result: any) {
                setIsSubscribed(result.Response);
                setIsLoading(false);
            },
            onError: (error: any) => {
                setIsLoading(false);
            },
        });
    }, []);

    if (isSubscribed) {
        return null;
    }

    const joinHandler = () => {
        setIsLoading(true);
        subscribeUser({
            onSuccess() {
                setIsSubscribed(false);
                setIsSuccessfullySubscribed(true);
                setIsLoading(false);
            },
            onError: () => {
                setIsLoading(false);
                setErrorMessage("Error happened while subscribing, or the current user is not valid for subscription.");
            },
        });
    };

    return (
        <LoadingOverlay {...styles.loadingOverlay} loading={isLoading}>
            <StyledWrapper {...styles.container}>
                <StyledWrapper {...styles.joinWrapper}>
                    {!isSuccessfullySubscribed ? (
                        <>
                            <Typography {...styles.heading}>{translate("Let's Stay in Touch")}</Typography>
                            <Typography {...styles.joinInfo}>
                                {translate(
                                    "Sign up to receive emails for exclusive offers, product related news, and timely information from Royal Canin.",
                                )}
                            </Typography>
                            <Typography {...styles.agreeToJoinMessage}>
                                {siteMessage("Custom_Agree_Joining")}
                            </Typography>
                            {errorMessage && <Notify type="danger">{errorMessage}</Notify>}
                            <Button variant="primary" onClick={joinHandler}>
                                {translate("Sign Up")}
                            </Button>
                        </>
                    ) : (
                        <>
                            <Notify type="success">{siteMessage("Custom_JoinToday_SuccessMessage")}</Notify>
                            <Typography {...styles.UpdatePrefrencesMessage}>
                                {siteMessage("Custom_JoinToday_UpdatePrefrencesMessage")}
                            </Typography>
                        </>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </LoadingOverlay>
    );
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(JoinToday),
    definition: {
        group: "XNGAGE" as any,
        displayName: "Join Today",
    },
};

export default widgetModule;
